import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useResponsavelAlunoContext } from '@/contexts/ResponsavelAlunoContext';
import { getDefaultRoute } from '@/utils/getDefaultRoute';
import { Loading } from '@/components/ui/loading';

interface ResponsavelGuardProps {
  children: React.ReactNode;
  requireAlunoSelecionado?: boolean;
  alunoId?: number; // ID do aluno sendo acessado (para validar se pertence ao responsável)
}

/**
 * Guard que protege rotas específicas para responsáveis
 * Verifica se o usuário tem role RESPONSAVEL e valida acesso aos dados
 */
export function ResponsavelGuard({
  children,
  requireAlunoSelecionado = false,
  alunoId,
}: ResponsavelGuardProps) {
  const { hasRole, isSuperAdmin, isLoading: authLoading, user } = useAuth();
  const { alunos, alunoSelecionado, isLoading: alunosLoading } = useResponsavelAlunoContext();

  // Aguardar carregamento da autenticação
  if (authLoading || alunosLoading) {
    return <Loading size="lg" message="Verificando permissões..." />;
  }

  // Super admin tem acesso (para testes/depuração)
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Verificar se tem role RESPONSAVEL
  if (!hasRole('RESPONSAVEL')) {
    // Redirecionar para rota padrão baseada no role do usuário
    const defaultRoute = getDefaultRoute(user);
    return <Navigate to={defaultRoute} replace />;
  }

  // Se requer aluno selecionado e não há nenhum selecionado
  if (requireAlunoSelecionado && !alunoSelecionado) {
    // Se houver apenas um aluno, selecionar automaticamente
    if (alunos.length === 1) {
      // O contexto já deve fazer isso, mas vamos garantir
      return <>{children}</>;
    }
    // Redirecionar para dashboard para selecionar aluno
    return <Navigate to="/app/responsavel" replace />;
  }

  // Se foi passado alunoId, validar se o aluno pertence ao responsável
  if (alunoId) {
    const alunoPertence = alunos.some((a) => a.alunoId === alunoId && a.ativo);
    if (!alunoPertence) {
      // Aluno não pertence ao responsável ou não está ativo
      return <Navigate to="/app/responsavel" replace />;
    }
  }

  return <>{children}</>;
}
